// apps/web/src/hooks/useLobbyHeartbeat.ts
import { useEffect, useRef } from 'react';
import { getValidAccessToken } from '../lib/session.js';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
const PULSE_DEBOUNCE_MS = 5_000;
const ENDPOINT = '/api/lobby/heartbeat';

export interface UseLobbyHeartbeatOptions { enabled: boolean }
export interface UseLobbyHeartbeatResult { pulse: () => void }

export function useLobbyHeartbeat(opts: UseLobbyHeartbeatOptions): UseLobbyHeartbeatResult {
  const { enabled } = opts;
  const lastPostRef = useRef<number>(0);
  const pulseRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let inFlight = false;

    const post = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        const token = await getValidAccessToken();
        if (cancelled || !token) return;
        lastPostRef.current = Date.now();
        try {
          await fetch(ENDPOINT, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) {
          console.warn('useLobbyHeartbeat: post failed', err);
        }
      } finally {
        inFlight = false;
      }
    };

    pulseRef.current = () => {
      if (Date.now() - lastPostRef.current < PULSE_DEBOUNCE_MS) return;
      void post();
    };

    void post();
    const id = setInterval(() => { void post(); }, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
      pulseRef.current = () => {};
    };
  }, [enabled]);

  return { pulse: () => pulseRef.current() };
}
